import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Crown, ArrowUpRight, Eye, EyeOff, Lock, Loader2 } from "lucide-react";

export default function AdminAuth() {
  const nav = useNavigate();
  const { user, isPreview, previewLogin, registerAdmin } = useAuth();
  const [adminExists, setAdminExists] = useState(null); // null = loading
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ username: "", password: "", first_name: "Admin", last_name: "", email: "" });
  const [showPassword, setShowPassword] = useState(false);

  // Preview-mode password state
  const [previewPassword, setPreviewPassword] = useState("");
  const [showPreviewPw, setShowPreviewPw] = useState(false);

  useEffect(() => {
    api.get("/auth/admin/exists").then(({ data }) => {
      const exists = !!data.exists;
      setAdminExists(exists);
      if (!isPreview && exists) {
        // Production: admin exists → all admin login goes through member login.
        if (user && (user.role === "admin" || user.role === "primary_admin")) {
          nav("/a", { replace: true });
        } else {
          nav("/login", { replace: true });
        }
      }
      // Preview mode: always show the preview password form regardless of admin existence
    }).catch(() => {
      setAdminExists(false);
    });
  }, [nav, user, isPreview]);

  // If already logged in as admin, redirect
  useEffect(() => {
    if (user && (user.role === "admin" || user.role === "primary_admin")) {
      nav("/a", { replace: true });
    }
  }, [user, nav]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const doRegister = async (e) => {
    e.preventDefault();
    if (!form.username || !form.password || !form.email) { toast.error("Username, password and email required"); return; }
    setBusy(true);
    try {
      const data = await registerAdmin(form);
      if (data.is_primary_admin) toast.success("Welcome — you are the Primary Admin!");
      else toast.success("Admin account created");
      nav("/a");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally { setBusy(false); }
  };

  const doPreviewLogin = async (e) => {
    e.preventDefault();
    if (!previewPassword.trim()) { toast.error("Password required"); return; }
    setBusy(true);
    try {
      await previewLogin(previewPassword.trim());
      nav("/a");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid password");
    } finally { setBusy(false); }
  };

  // Loading state
  if (adminExists === null) {
    return (
      <PublicLayout>
        <div className="relative min-h-[85vh] flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="relative min-h-[85vh] flex items-center justify-center">
        <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
        <div className="absolute inset-0 gradient-radial-accent pointer-events-none" />
        <div className="relative w-full max-w-md px-5 sm:px-6 py-10 sm:py-12">
          {isPreview ? (
            /* Preview mode — simple password gate */
            <Card className="p-6 sm:p-8 rounded-2xl shadow-lg">
              <div className="flex items-center justify-center h-12 w-12 rounded-xl bg-[hsl(var(--fg))] text-[hsl(var(--bg))] mb-5 mx-auto">
                <Lock className="h-5 w-5" />
              </div>
              <div className="text-center">
                <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Preview Mode</span>
                <h1 className="mt-2 text-2xl font-bold tracking-tight">Admin Access</h1>
                <p className="text-sm text-muted-foreground mt-1.5">Enter the admin password to continue</p>
              </div>
              <form onSubmit={doPreviewLogin} className="mt-6 space-y-4">
                <div>
                  <Label htmlFor="preview-pw" className="text-sm font-medium">Password</Label>
                  <div className="relative">
                    <Input
                      id="preview-pw"
                      data-testid="preview-admin-password"
                      type={showPreviewPw ? "text" : "password"}
                      value={previewPassword}
                      onChange={(e) => setPreviewPassword(e.target.value)}
                      className="mt-1.5 h-11 rounded-lg pr-10"
                      autoFocus
                      placeholder="Enter admin password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPreviewPw((v) => !v)}
                      className="absolute right-0 top-0 h-full px-3 text-muted-foreground hover:text-foreground transition-colors duration-150"
                      aria-label={showPreviewPw ? "Hide password" : "Show password"}
                      tabIndex={-1}
                      data-testid="preview-toggle-password-visibility"
                    >
                      {showPreviewPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={busy || !previewPassword.trim()}
                  className="w-full h-12 btn-accent-modern mt-2"
                  data-testid="preview-admin-submit"
                >
                  {busy ? "Signing in…" : <>Enter Admin Portal <ArrowUpRight className="ml-1.5 h-4 w-4" /></>}
                </Button>
              </form>
            </Card>
          ) : adminExists ? (
            /* Admin exists — user is being redirected to /login. Show a brief message. */
            <Card className="p-6 sm:p-8 rounded-2xl shadow-lg text-center">
              <div className="flex items-center justify-center h-12 w-12 rounded-xl bg-[hsl(var(--fg))] text-[hsl(var(--bg))] mb-5 mx-auto">
                <Crown className="h-5 w-5" />
              </div>
              <h1 className="text-2xl font-bold tracking-tight">Admin Access</h1>
              <p className="text-sm text-muted-foreground mt-2">All admins log in through the member login. You'll find the Admin Portal on your member dashboard.</p>
              <Button onClick={() => nav("/login")} className="w-full h-12 btn-primary-modern mt-6" data-testid="admin-goto-member-login">
                Go to Member Login <ArrowUpRight className="ml-1.5 h-4 w-4" />
              </Button>
            </Card>
          ) : (
            /* No admin exists yet — show bootstrap registration */
            <Card className="p-6 sm:p-8 rounded-2xl shadow-lg">
              <div className="flex items-center justify-center h-12 w-12 rounded-xl bg-[hsl(var(--fg))] text-[hsl(var(--bg))] mb-5">
                <Crown className="h-5 w-5" />
              </div>
              <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">First-time setup</span>
              <h1 className="mt-2 text-3xl font-bold tracking-tight">Create <span className="font-serif-display italic text-gradient-accent">primary admin</span></h1>
              <p className="text-sm text-muted-foreground mt-1.5">You're the first to register — you'll be the Primary Admin.</p>
              <form onSubmit={doRegister} className="mt-6 space-y-4">
                <div>
                  <Label htmlFor="username" className="text-sm font-medium">Username</Label>
                  <Input id="username" data-testid="admin-register-username" value={form.username} onChange={(e) => set("username", e.target.value)} className="mt-1.5 h-11 rounded-lg" />
                </div>
                <div>
                  <Label htmlFor="email" className="text-sm font-medium">Email</Label>
                  <Input id="email" data-testid="admin-register-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5 h-11 rounded-lg" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="fn" className="text-sm font-medium">First name</Label>
                    <Input id="fn" data-testid="admin-register-firstname" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className="mt-1.5 h-11 rounded-lg" />
                  </div>
                  <div>
                    <Label htmlFor="ln" className="text-sm font-medium">Last name</Label>
                    <Input id="ln" data-testid="admin-register-lastname" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className="mt-1.5 h-11 rounded-lg" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="pw" className="text-sm font-medium">Password</Label>
                  <div className="relative">
                    <Input id="pw" data-testid="admin-register-password" type={showPassword ? "text" : "password"} value={form.password} onChange={(e) => set("password", e.target.value)} className="mt-1.5 h-11 rounded-lg pr-10" />
                    <button type="button" onClick={() => setShowPassword((v) => !v)} className="absolute right-0 top-0 h-full px-3 text-muted-foreground hover:text-foreground transition-colors duration-150" aria-label={showPassword ? "Hide password" : "Show password"} tabIndex={-1} data-testid="admin-toggle-register-password-visibility">
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" disabled={busy} className="w-full h-12 btn-accent-modern mt-2" data-testid="admin-register-submit">{busy ? "Creating…" : <>Create Primary Admin <ArrowUpRight className="ml-1.5 h-4 w-4" /></>}</Button>
              </form>
            </Card>
          )}
        </div>
      </div>
    </PublicLayout>
  );
}
